"use client";

import { CreateProjectModal } from "@/components/features/projects/CreateProjectModal";
import { useProjectsRootStore } from "@/stores/projects/createProjectsRootStore";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ProjectsPageView } from "./components/ProjectsPageView";
import { useProjectManualOrder } from "./hooks/useProjectManualOrder";
import type { Project } from "./types/Project";
import { useAdmin } from "@/contexts/AdminContext";
import { useFrontendContainer } from "@/infrastructure/di/FrontendProvider";
import { useAuthState } from "react-firebase-hooks/auth";
import { auth } from "@/lib/firebase";
import { ProjectsApiAuthError } from "@/lib/projectsApi";
import { handleProjectAuthError } from "@/infrastructure/auth/authUtils";
import { useWebMcpTools } from "@/lib/webmcp/useWebMcpTools";
import { projectsTools } from "@/lib/webmcp/tools";
import { openWorkspaceRoute, waitForVisibleState } from "@/lib/webmcp/visible-state";

export const ProjectsPageContainer = observer(function ProjectsPageContainer() {
  const router = useRouter();
  const projectsStore = useProjectsRootStore();
  const handleProjectAuthErrorIfNeeded = useCallback((error: unknown): boolean => {
    if (error instanceof ProjectsApiAuthError) {
      handleProjectAuthError(error.code);
      return true;
    }
    return false;
  }, []);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  useWebMcpTools(projectsTools(
    projectsStore,
    (path, selector) => openWorkspaceRoute(path, router.push, selector),
    async () => {
      setIsCreateModalOpen(true);
      await waitForVisibleState(() => document.querySelector('[data-webmcp-create-project]') !== null);
    },
    (id) => waitForVisibleState(() => document.querySelector('[data-webmcp-projects]')?.getAttribute('data-webmcp-projects')?.split(',').includes(id) === true),
  ), "projects");
  const { isAdmin, isAdminViewEnabled } = useAdmin();
  const container = useFrontendContainer();
  const getAllProjectsAdminUseCase = useMemo(
    () => container.resolveGetAllProjectsAdminUseCase(),
    [container]
  );
  const [user] = useAuthState(auth!);
  const [adminProjects, setAdminProjects] = useState<Project[]>([]);
  const [isAdminLoading, setIsAdminLoading] = useState(false);
  const [adminError, setAdminError] = useState<string | null>(null);
  const [adminSearchValue, setAdminSearchValue] = useState("");
  const mountTimeRef = useRef<number | null>(null);
  const {
    orderedIds,
    syncWithProjectIds,
    reorder,
    isReorderEnabled,
  } = useProjectManualOrder(user?.uid);

  const loadProjects = useCallback(
    async (force = false): Promise<void> => {
      try {
        await projectsStore.loadProjects(force);
      } catch (error) {
        if (handleProjectAuthErrorIfNeeded(error)) {
          return;
        }

        const message =
          error instanceof Error ? error.message : "Failed to load projects";
        console.error(message);
      }
    },
    [handleProjectAuthErrorIfNeeded, projectsStore]
  );

  // Load projects on mount (only if not already loaded or data is stale)
  useEffect(() => {
    // Check if data needs to be loaded
    const needsLoad = !projectsStore.hasBeenFetched || 
      (projectsStore as unknown as { listStore: { isDataStale: boolean } }).listStore?.isDataStale;
    
    if (needsLoad) {
      void loadProjects();
    }
    mountTimeRef.current = Date.now();
  }, [loadProjects, projectsStore]);

  // Reload projects when page gains focus (only after initial mount delay)
  useEffect(() => {
    const handleFocus = () => {
      // Don't reload if component just mounted (within 2 seconds)
      if (mountTimeRef.current === null) {
        return;
      }
      
      const timeSinceMount = Date.now() - mountTimeRef.current;
      if (timeSinceMount < 2000) {
        return;
      }
      
      // Don't reload if already loading
      if (projectsStore.isLoading) {
        return;
      }

      // Reload projects when page gains focus to get latest data
      void loadProjects(true);
    };

    window.addEventListener("focus", handleFocus);
    return () => {
      window.removeEventListener("focus", handleFocus);
    };
  }, [loadProjects, projectsStore]);

  useEffect(() => {
    const loadAdminProjects = async () => {
      if (!isAdmin) {
        setAdminProjects([]);
        setAdminError(null);
        return;
      }
      setIsAdminLoading(true);
      setAdminError(null);
      try {
        const result = await getAllProjectsAdminUseCase.execute();
        const mapped: Project[] = result.map((p) => ({
          id: p.id,
          title: p.name,
          description: p.description || undefined,
          status: p.status,
          recordsCount: p.recordsCount,
          projectReportsCount: p.projectReportsCount,
          activeInterviewRoomsCount: p.activeInterviewRoomsCount ?? 0,
          createdAt: p.createdAt,
          ownerEmail: p.ownerEmail,
        }));
        setAdminProjects(mapped);
      } catch (error) {
        if (handleProjectAuthErrorIfNeeded(error)) {
          return;
        }

        const message = error instanceof Error ? error.message : "Failed to load admin projects";
        setAdminError(message);
        console.error(message);
      } finally {
        setIsAdminLoading(false);
      }
    };

    void loadAdminProjects();
  }, [getAllProjectsAdminUseCase, handleProjectAuthErrorIfNeeded, isAdmin]);

  const handleCreateProject = useCallback(() => {
    setIsCreateModalOpen(true);
  }, []);

  const handleProjectClick = useCallback(
    (projectId: string) => {
      router.push(`/space/projects/${projectId}`);
    },
    [router]
  );

  const handleHowItWorksClick = useCallback(() => {
    // TODO: Implement how it works modal/page
    console.info("How it works documentation coming soon");
  }, []);

  const handleCloseCreateModal = useCallback(
    async (shouldReload?: boolean) => {
      setIsCreateModalOpen(false);
      // Only reload if a project was actually created
      if (shouldReload) {
        await loadProjects(true);
      }
    },
    [loadProjects]
  );

  // Map ProjectDTOs to UI Project types
  const projects: Project[] = useMemo(
    () =>
      projectsStore.allProjects.map((p) => ({
        id: p.id,
        title: p.name, // DTO uses 'name', UI uses 'title'
        description: p.description || undefined,
        status: p.status,
        recordsCount: p.recordsCount,
        projectReportsCount: p.projectReportsCount,
        activeInterviewRoomsCount: p.activeInterviewRoomsCount ?? 0,
        createdAt: p.createdAt,
      })),
    [projectsStore.allProjects]
  );

  const visibleAdminProjects: Project[] = useMemo(
    () =>
      adminProjects.filter((p) => !user?.email || p.ownerEmail !== user.email),
    [adminProjects, user?.email]
  );

  return (
    <>
      <ProjectsPageView
        projects={projects}
        isLoading={projectsStore.isLoading}
        isAdmin={isAdmin && isAdminViewEnabled}
        adminProjects={visibleAdminProjects}
        isAdminLoading={isAdminLoading}
        adminError={adminError}
        adminSearchValue={adminSearchValue}
        onAdminSearchChange={setAdminSearchValue}
        onCreateProject={handleCreateProject}
        onProjectClick={handleProjectClick}
        onHowItWorksClick={handleHowItWorksClick}
        orderedProjectIds={orderedIds}
        onSyncProjectOrder={syncWithProjectIds}
        onReorderProjects={reorder}
        isReorderEnabled={isReorderEnabled}
      />

      <CreateProjectModal
        isVisible={isCreateModalOpen}
        onClose={handleCloseCreateModal}
      />
    </>
  );
});
